import { useState, useEffect, useRef } from 'react';
import {Box, Typography, Paper, Divider, Button, IconButton, useTheme, CardContent} from '@mui/material';
import { ArrowBackIos, ArrowForwardIos } from '@mui/icons-material';
import { getLogoUrl } from "../../utilities/consts.js";
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CancelIcon from '@mui/icons-material/Cancel';
import FiberManualRecordIcon from '@mui/icons-material/FiberManualRecord';
import { getPlatform, useScreenOrientation } from "../../utilities/orientationAndPlatform.js"; 



export default function StationCards({ marketData }) {
  const orientation = useScreenOrientation();
  const [selectedStation, setSelectedStation] = useState(null);
  const [internalMarketKey, setInternalMarketKey] = useState(0);
  const scrollRef = useRef(null);
  const theme = useTheme();

  useEffect(() => {
  if (marketData && marketData.stations?.length > 0) {
      setSelectedStation(marketData.stations[0]);
      setInternalMarketKey((prev) => prev + 1);
    } else {
      setSelectedStation(null);
    }
  }, [marketData]);

  let cardWidth;
  const platform = getPlatform();
  if (platform === "mobile") {
      if (orientation === 0) {
          cardWidth = 300;
      }
      if (orientation === 90) {
              cardWidth = 350;
          }
  }
  if (platform === "desktop") {
         cardWidth = 400;
  }
  

  const scrollBy = (distance) => {
    if (scrollRef.current) {
      scrollRef.current.scrollBy({ left: distance, behavior: 'smooth' });
    }
  };
  // If no market data is provided, show a default message
  if (!marketData) {                                                                                           
    return (
      <Box sx={{ width: cardWidth, height: '100%', display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
        <Typography variant="h5" sx={{ color: theme.palette.text.primary, mb: 2 }}>
          Click on a market to view its stations
        </Typography>
        <Paper
          elevation={6}
          sx={{
            p: 3,
            border: '1px solid black',
            borderRadius: 4,
            backgroundColor: theme.palette.background.paper,
            width: '100%',
            height: '100%',
            display: 'flex',
            flexDirection: 'column',
          }}
        >
          <Typography variant="h4" sx={{ color: theme.palette.text.primary, mb: 2 }}>
            Connecting people with content everywhere!
          </Typography>
          <Typography variant="h5" sx={{color: theme.palette.text.primary, mb: 2}}>
            <strong>Local Focus, National Reach</strong>
          </Typography>
          <Typography variant="body1" sx={{color: theme.palette.text.primary, mb: 2}}>
            185 Owned or Operated Stations in 86 Markets
          </Typography>
           <Typography variant="body1" sx={{color: theme.palette.text.primary, mb: 2}}>
            Sinclair is one of the largest and most diversified television broadcasters with a local focus and a national reach.
          </Typography>
          <Typography
              variant="body2"
              sx={{ fontStyle: 'italic', color: theme.palette.text.secondary }}
            >
              Click on a station to view its details
            </Typography>
        </Paper>
      </Box>
    );
  }


  // If market data is provided, render the station card
  return (

    <Box sx={{ width: cardWidth, height: '100%', display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
      <Typography variant="h5" sx={{ color: theme.palette.text.primary, mb: 2 }}>
        {marketData.market}
      </Typography>

      <Paper
        key={internalMarketKey}
        elevation={6}
        sx={{
          p: 3,
          border: '1px solid black',
          borderRadius: 4,
          backgroundColor: theme.palette.background.TableCell,
          width: '100%',
          height: '100%',
          display: 'flex',
          flexDirection: 'column',
        }}
      >
        <Typography variant="h6" sx={{ color: theme.palette.text.primary, mb: 2 }}>
          Stations in This Market
        </Typography>

        {/* Navigation Arrows + Scrollable Row */}
        <Box sx={{ position: 'relative', mb: 2 }}>
          <IconButton
            onClick={() => scrollBy(-150)}
            sx={{ position: 'absolute', left: 0, top: '50%', transform: 'translateY(-50%)', zIndex: "1" }}
          >
            <ArrowBackIos fontSize="small" />
          </IconButton>

          <Box
            ref={scrollRef}
            sx={{
              display: 'flex',
              overflowX: 'auto',
              gap: 2,
              scrollbarWidth: 'none',
              msOverflowStyle: 'none',
              pl: 4,
              pr: 4,
              '&::-webkit-scrollbar': {
                display: 'none',
              },
            }}
          >
            {marketData.stations.map((station) => (
              <Button
                key={station.call_sign + station.affiliation}
                onClick={() => setSelectedStation(station)}
                disableElevation
                disableRipple
                sx={{
                  minWidth: platform === "mobile" ? 200:  "7vw",
                  maxHeight: '18vh',
                  minHeight: '14vh',
                  cursor: 'pointer',
                  borderRadius: 2,
                  flexShrink: 0,
                  padding: 1,
                  display: "flex",
                  flexDirection: "column",
                  justifyContent: "center",
                  alignItems: "center",
                  textTransform: "none",
                  border:
                    selectedStation === station
                      ? `2px solid ${theme.palette.primary.main}`
                      : `1px solid ${theme.palette.divider}`,
                  backgroundColor:
                    selectedStation === station
                      ? theme.palette.action.selected
                      : theme.palette.background.paper,
                  transition: '0.3s ease',
                }}
              >
                {/* Icon in top-left corner */}
              {station.live && (
              <Box
                sx={{
                  position: 'absolute',
                  top: 8,
                  left: 8,
                  display: 'flex',
                  alignItems: 'center',
                  gap: 0.5,
                }}
              >
                <FiberManualRecordIcon sx={{ fontSize: 18, color: '#ff1744' }} />
                LIVE
              </Box>)}
                <CardContent sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', pl: station.live ? 4 : 2 , pt: 2, pb: 2}}>
                  <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
                    {station.call_sign}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {station.affiliation}
                  </Typography>
                </CardContent>
              </Button>
            ))}
          </Box>

          <IconButton
            onClick={() => scrollBy(150)}
            sx={{ position: 'absolute', right: 0, top: '50%', transform: 'translateY(-50%)' }}
          >
            <ArrowForwardIos fontSize="small" />
          </IconButton>
        </Box>
        
        {selectedStation && selectedStation.channel_id ? (
          <>
            <Divider sx={{ my: 2 }} />
            <Box
              sx={{
                backgroundColor: '#1F3F66',
                borderRadius: 2,
                p: 1, 
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
              }}
            >
            <img
              src={`${getLogoUrl}/${selectedStation.channel_id}.svg`}
              alt={`${selectedStation.channel_id}'s logo`}
              style={{ width: '100%', height: '5rem', borderRadius: 4 }}
              onError={(e) => {
                e.target.onerror = null; 
                e.target.src = 'logos/sinclair-logo-blue.svg'; 
              }}
            /></Box>
          </>
        ) : null}
        


        <Divider sx={{ my: 2 }} />

        <Box sx={{ maxHeight: 200, overflowY: 'auto' }}>
          {selectedStation ? (
            <>
              <Typography variant="subtitle2">
                <strong>Call Sign:</strong> {selectedStation.call_sign}
              </Typography>
              <Typography variant="subtitle2">
                <strong>Affiliation:</strong> {selectedStation.affiliation}
              </Typography>
              {selectedStation.site ? (
                <Typography variant="subtitle2">
                  <strong>Website:</strong> 
                  <a href={selectedStation.site} target='_blank' rel='noopener noreferrer'>
                    {selectedStation.site}
                  </a>
                </Typography>
              ) : null}
              <Typography variant="subtitle2"  style={{ display: 'flex', alignItems: 'center' }}>
                <strong>ATSC 1.0:</strong> {selectedStation.atsc1 ? (<CheckCircleIcon sx={{ marginRight: 8, verticalAlign: 'middle', color: "green" }} ></CheckCircleIcon >) : (<CancelIcon sx={{ marginRight: 8, verticalAlign: 'middle' , color:"red"}} ></CancelIcon>)}
              </Typography>
              <Typography variant="subtitle2">
                <strong>Users:</strong> {selectedStation.value}
              </Typography>
               <Typography variant="subtitle2">
                <strong>Live:</strong>{selectedStation.live ? (<CheckCircleIcon sx={{ marginRight: 8, verticalAlign: 'middle', color: "green" }} ></CheckCircleIcon >) : (<CancelIcon sx={{ marginRight: 8, verticalAlign: 'middle' , color:"red"}} ></CancelIcon>)}
              </Typography>
            </>
          ) : (null)}
        </Box>
      </Paper>
    </Box>
  );
}
